// The online FrameView source (§23.2, §23.4, §20.7–§20.11). Routes server messages to the bus and
// the netcode modules; every frame it reconciles and advances prediction, sends inputs, advances
// the interpolation timeline, dispatches due events and assembles the FrameView in place.
// DOM- and three-free (it takes inputs through `InputSource`), so Node clients can run it too.
import { BotManager } from '@shared/bots/manager';
import type { BotContext } from '@shared/bots/botTypes';
import { MAX_BEANS, NO_SLOT, SNAPSHOT_EVERY } from '@shared/constants';
import { EV, KoFlag, type SimEvent } from '@shared/events';
import { BS, Difficulty, ModeId, Personality, RoomPhase } from '@shared/ids';
import { roundClock } from '@shared/maps/clock';
import { MAPS } from '@shared/maps/index';
import { clamp } from '@shared/math';
import type { MatchEndDTO, RoomStateDTO, RoundEndDTO, RoundInitDTO, ServerMsg } from '@shared/protocol/messages';
import { decodeSnapshot, readSnapshotHeader, type Snapshot, type SnapshotHeader } from '@shared/protocol/snapshot';
import { hashWorld } from '@shared/sim/world';
import { NEUTRAL_INPUT, type InputFrame, type Vec2, type Vec3, type World } from '@shared/types';
import { onSim, type Bus } from '../core/bus';
import { FrameTimer } from '../core/time';
import type { AimContext } from '../input/InputManager';
import { ClockSync } from '../net/ClockSync';
import type { NetClient } from '../net/Net';
import { SnapshotBuffer } from '../net/SnapshotBuffer';
import { EventScheduler } from './EventScheduler';
import {
  beanViewFromBean, copyMapState, createFrameView, fillFrameFromWorld, fillModeView, frameGrade,
  resizeEntityViews, setBeanIdentity, setRoundInfo, type BeanView, type FrameView,
} from './FrameView';
import { Interpolation } from './Interpolation';
import { ErrorStats, PendingEntities, PREDICTION, Prediction, slackTarget, type PredErrSummary } from './Prediction';
import { ReplayBuffer, type ReplaySlice } from './ReplayBuffer';

/** Where local input frames come from. `InputManager` satisfies it. */
export interface InputSource { sample(tick: number, aim: AimContext): InputFrame }

export interface ClientGameDeps {
  net: NetClient;
  bus: Bus;
  /** Local input; null for a client that only watches (and for autoplay-only clients). */
  input?: InputSource | null;
}

/** `__SHOVEOFF__.net()` shape (§28). */
export interface NetDebug { rtt: number; jitter: number; predErr: PredErrSummary; snapBytesAvg: number; online: boolean }
/** `__SHOVEOFF__.tick()` shape (§28): room tick of the newest snapshot, then the view's ticks. */
export interface TickDebug { room: number; round: number; render: number; local: number }

export const CLIENT_GAME = {
  /** A longer gap between frames (backgrounded tab, debugger) forces a hard resync (§20.5). */
  resyncGapMs: 1000,
  /** Inputs go out every 2 ticks with 2 redundant frames (§20.3). */
  sendEveryTicks: 2,
  redundantFrames: 2,
  maxFramesPerBatch: 8,
  /** A round further than this past its start when `round` arrives was joined midway (§20.11). */
  maxOpeningTicks: 60,
  /** §20.6: the server repeats the last frame it received for this many ticks, then goes neutral. */
  serverRepeatTicks: 6,
  /** A snapshot can wait up to one frame for the next `pump()`; the render buffer allows for it. */
  frameMarginTicks: 1,
} as const;

/** Room phases in which the server steps the world (§21.5). */
const STEPPING_PHASES: readonly RoomPhase[] = [RoomPhase.LOBBY, RoomPhase.INTRO, RoomPhase.PLAY, RoomPhase.ROUND_END, RoomPhase.PODIUM];
/**
 * Room phases in which the local bean is predicted (§20.7). ROUND_END is added to the spec's list:
 * survivors keep moving for that second (§9.5), and switching to interpolation at the final KO would
 * slide the local bean back by the round trip just as the slow motion plays.
 */
const PREDICTED_PHASES: readonly RoomPhase[] = [RoomPhase.LOBBY, RoomPhase.INTRO, RoomPhase.PLAY, RoomPhase.ROUND_END, RoomPhase.PODIUM];
/** Local states rendered by interpolation: someone else moves the bean (§20.7). */
const UNPREDICTED_STATES: readonly BS[] = [BS.GRABBED, BS.YANKED, BS.SPUN];
/** Autoplay personality by slot. */
const PERSONALITIES: readonly Personality[] = Object.values(Personality);

interface Session { code: string; slot: number; spectator: boolean }

export class ClientGame {
  readonly view: FrameView = createFrameView();
  readonly clock = new ClockSync();
  readonly snapshots = new SnapshotBuffer();
  readonly interp = new Interpolation();
  readonly events: EventScheduler;
  readonly replay = new ReplayBuffer();
  /** Reconciliation error over the whole session. */
  readonly errors = new ErrorStats();

  private readonly net: NetClient;
  private readonly bus: Bus;
  private input: InputSource | null;
  private groundFromScreen: (sx: number, sy: number, out: Vec2) => boolean = () => false;
  private readonly pending = new PendingEntities();
  private pred: Prediction | null = null;
  private errorProbe: ((err: number, p: Prediction) => void) | null = null;
  private session: Session | null = null;
  private roomState: RoomStateDTO | null = null;
  private roundInit: RoundInitDTO | null = null;
  private roundToRoom = 0;
  private readonly timer = new FrameTimer();
  private lastFrameAt = -1;
  private resyncRequested = false;
  private lastResetAuthTick = -1;
  private lastSampledTick = 0;
  private lastSentTick = 0;
  /** The newest frame sent: what the server falls back to when later frames are missing (§20.6). */
  private readonly lastSent: InputFrame = { tick: -1, ...NEUTRAL_INPUT };
  /** Frames for ticks up to this would reach the server after it applied them (set per frame). */
  private lateBefore = Number.NEGATIVE_INFINITY;
  private viewTick = 0;
  private predicting = false;
  private gameOver = false;
  /** The round's opening ticks have not been stepped yet (see `openRound`). */
  private opening = false;
  private focus = NO_SLOT;
  private ghostSpectate = false;
  /** Until this time (ms) the local bean's corrections are absorbed fast (a hit just arrived). */
  private hitAbsorbUntil = Number.NEGATIVE_INFINITY;
  private instantSlice: ReplaySlice | null = null;
  private potmSlice: ReplaySlice | null = null;
  private hasLocalRender = false;
  private renderedPredicted = false;
  private readonly lastLocalRender: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly localRaw: Vec3 = { x: 0, y: 0, z: 0 };
  private predErrCount = -1;
  private predErrP95 = 0;
  private badSnapshots = 0;
  private autoplay: BotManager | null = null;
  private readonly botFrames: (InputFrame | null)[] = new Array<InputFrame | null>(MAX_BEANS).fill(null);
  private readonly botCtx: BotContext = { matchScores: new Array<number>(MAX_BEANS).fill(0), crowned: NO_SLOT, humanSlots: [], reactionMult: 1, teamsOn: false };
  private readonly sampled: InputFrame = { tick: 0, ...NEUTRAL_INPUT };
  private readonly sendBuf: InputFrame[] = [];
  private readonly aim: AimContext;
  private readonly aimTargets: AimContext['targets'] = [];
  private readonly unsubscribe: (() => void)[];

  constructor(deps: ClientGameDeps) {
    this.net = deps.net;
    this.bus = deps.bus;
    this.input = deps.input ?? null;
    this.events = new EventScheduler(deps.bus);
    this.view.source = 'online';
    this.aim = { selfX: 0, selfY: 0, facing: 0, groundFromScreen: (sx, sy, out) => this.groundFromScreen(sx, sy, out), targets: [] };
    const net = deps.net;
    this.unsubscribe = [
      net.on('joined', (m, at) => this.onJoined(m, at)),
      net.on('room', (m) => this.onRoom(m.room)),
      net.on('round', (m) => this.onRound(m.init)),
      net.on('roundEnd', (m) => this.onRoundEnd(m.end)),
      net.on('matchEnd', (m) => this.onMatchEnd(m.end)),
      net.on('pong', (m, at) => this.clock.addPong(m.c, m.tick, m.frac, at)),
      net.on('toast', (m) => this.bus.emit('toast', { text: m.text, kind: m.kind })),
      net.on('error', (m) => this.bus.emit('error', { code: m.code, msg: m.msg })),
      net.on('kicked', (m) => this.onKicked(m)),
      net.on('snapshot', (data, at) => this.onSnapshot(data, at)),
      onSim(this.bus, (ev, _local, replay) => {
        if (!replay) this.watchEvent(ev);
      }),
    ];
  }

  // ---- Public state ---------------------------------------------------------------------------

  get inRoom(): boolean {
    return this.session !== null;
  }

  /** True once the current round has its first snapshot (before that, show "Connecting…"). */
  get ready(): boolean {
    return this.pred !== null && this.pred.hasAuth;
  }

  get roomCode(): string | null {
    return this.session?.code ?? null;
  }

  /** The local bean's slot; NO_SLOT for spectators and outside a room. */
  get localSlot(): number {
    return this.session && !this.session.spectator ? this.session.slot : NO_SLOT;
  }

  get spectator(): boolean {
    return this.session?.spectator ?? false;
  }

  get isHost(): boolean {
    return this.session !== null && this.roomState !== null && this.roomState.hostSlot === this.session.slot;
  }

  get room(): RoomStateDTO | null {
    return this.roomState;
  }

  get round(): RoundInitDTO | null {
    return this.roundInit;
  }

  /** The newest authoritative world (the last applied snapshot), or null outside a round. */
  get authWorld(): World | null {
    return this.pred?.authWorld ?? null;
  }

  /** The local bean was drawn from prediction on the last frame (false: interpolated, or none). */
  get localPredicted(): boolean {
    return this.predicting;
  }

  /** Current input lead over the server clock, ticks (NaN before the round's first snapshot). */
  get lead(): number {
    return this.pred?.lead ?? Number.NaN;
  }

  // ---- Control --------------------------------------------------------------------------------

  setInput(input: InputSource | null): void {
    this.input = input;
  }

  /** Aim support from the renderer: CSS px → sim ground plane. */
  setGroundFromScreen(fn: (sx: number, sy: number, out: Vec2) => boolean): void {
    this.groundFromScreen = fn;
  }

  /** `?autoplay=1`: a bot brain drives the local bean instead of the input source (§22). */
  setAutoplay(on: boolean): void {
    this.autoplay = on ? new BotManager() : null;
    if (this.autoplay && this.pred) this.armAutoplay(this.pred);
  }

  /** Diagnostics: observe every reconciliation error (soak probe). */
  setErrorProbe(fn: ((err: number, p: Prediction) => void) | null): void {
    this.errorProbe = fn;
    if (this.pred) this.pred.onError = fn;
  }

  /** Camera focus for spectators and ghost spectating; NO_SLOT returns to the default. */
  setFocus(slot: number): void {
    this.focus = slot;
  }

  /**
   * Ghost spectating (§9.6, the pause menu's "Spectate"): while the local bean is out, ghost control
   * is released (no inputs are sent) and the camera follows the bean that KO'd it, then any bean in
   * play; `cycleFocus` picks targets. Off again with `false`, at the next round, or when not out.
   */
  spectateAsGhost(on: boolean): void {
    const slot = this.localSlot;
    const me = slot !== NO_SLOT ? this.pred?.authWorld.beans[slot] : undefined;
    this.ghostSpectate = on && me !== undefined && me.out;
    this.focus = this.ghostSpectate && me ? me.koBy : NO_SLOT;
  }

  /** The last round's Instant Replay (its highlight window, §9.5), ready when `round:end` fires. */
  get instantReplay(): ReplaySlice | null {
    return this.instantSlice;
  }

  /** The match's Play of the Match (§9.7), ready when `match:end` fires; null if none. */
  get playOfMatch(): ReplaySlice | null {
    return this.potmSlice;
  }

  /** True while the player spectates as a ghost (see `spectateAsGhost`). */
  get ghostSpectating(): boolean {
    return this.ghostSpectate;
  }

  /** Cycles the focus through the beans still in play (spectator Left/Right). */
  cycleFocus(dir: 1 | -1): void {
    const start = this.view.focusSlot === NO_SLOT ? 0 : this.view.focusSlot;
    for (let i = 1; i <= MAX_BEANS; i++) {
      const slot = (start + dir * i + MAX_BEANS * 2) % MAX_BEANS;
      if (this.inPlay(this.view.beans[slot])) {
        this.focus = slot;
        return;
      }
    }
  }

  /** Requests a hard resync on the next frame (visibilitychange → visible, §20.5). */
  hardResync(): void {
    this.resyncRequested = true;
  }

  /** Leaves the room and forgets it. */
  leave(): void {
    this.net.send({ t: 'leaveRoom' });
    this.exitRoom();
  }

  dispose(): void {
    for (const off of this.unsubscribe) off();
    this.unsubscribe.length = 0;
  }

  // ---- Debug (§28) ----------------------------------------------------------------------------

  netDebug(): NetDebug {
    return { rtt: this.clock.rtt, jitter: this.clock.jitter, predErr: this.errors.summary(), snapBytesAvg: this.net.stats.snapBytesAvg, online: this.net.status === 'online' };
  }

  tickDebug(): TickDebug {
    return { room: this.snapshots.latest?.roomTick ?? 0, round: this.view.roundTick, render: this.view.renderTick, local: this.view.localTick };
  }

  /** `hashWorld` of the newest authoritative world (0 outside a round). */
  authHash(): number {
    return this.pred ? hashWorld(this.pred.authWorld) : 0;
  }

  /** The last `n` dispatched sim events. */
  recentEvents(n?: number): SimEvent[] {
    return this.events.recent(n);
  }

  /** Snapshots that failed to decode (server bug indicator). */
  get badSnapshotCount(): number {
    return this.badSnapshots;
  }

  // ---- Frame ----------------------------------------------------------------------------------

  /** Produces this frame's view (§23.2). `now` is the shared monotonic ms clock. */
  frame(now: number): FrameView {
    const v = this.view;
    const dt = this.timer.frame(now);
    const gap = this.lastFrameAt < 0 ? 0 : now - this.lastFrameAt;
    this.lastFrameAt = now;
    v.now = now;
    v.dt = dt;
    v.timeScale = 1;
    this.clock.update();
    if (this.session && this.net.status === 'online' && this.clock.pingDue(now)) this.net.send({ t: 'ping', c: now });

    const pred = this.pred;
    const init = this.roundInit;
    if (!pred || !init) {
      this.fillNetView();
      return v;
    }
    if (this.opening) this.openRound(now, pred);
    if (!pred.hasAuth) {
      fillFrameFromWorld(v, pred.predWorld, {
        now, dt, source: 'online', roomPhase: this.phase(), roundIndex: init.roundIndex, totalRounds: this.roomState?.totalRounds ?? 0,
        localSlot: this.localSlot, focusSlot: this.localSlot, tick: pred.predWorld.tick, players: this.roomState?.players ?? null,
        map: MAPS[init.cfg.mapId],
      });
      v.renderTick = v.localTick = pred.predTick;
      this.fillNetView();
      return v;
    }

    this.predicting = this.shouldPredict(pred);
    if (gap > CLIENT_GAME.resyncGapMs) this.resyncRequested = true;
    if (this.resyncRequested) this.resync(now, pred);

    const est = this.clock.estServerTick(now);
    // §20.8's timeline, kept inside the snapshot stream: never closer to the snapshots arriving now
    // than one snapshot interval, their arrival spread and a frame, so remotes are interpolated,
    // never dead-reckoned, however long the link (WP3 decision).
    const buffer = SNAPSHOT_EVERY + this.clock.arrivalSpread + CLIENT_GAME.frameMarginTicks;
    const renderTick = this.interp.update(est, this.clock.jitter, dt, this.clock.arrivalTick(now), buffer);
    this.viewTick = Math.max(0, Math.floor(renderTick));

    // Decay first: a correction added by this frame's reconciliation must render in full. Right
    // after a hit on the local bean the correction is absorbed under the hitstop instead.
    pred.decay(dt, now < this.hitAbsorbUntil ? PREDICTION.hitAbsorbTauSec : PREDICTION.offsetTauSec);
    let alpha = 1;
    if (this.worldSteps()) {
      pred.reconcile(this.predicting);
      const target = est + pred.lead;
      // The lead keeps `slackTarget` ticks of input slack at the server, so a frame sampled now for a
      // tick at or below `target − slackTarget` arrives after the server applied it (a stalled frame).
      this.lateBefore = target - slackTarget(this.clock.jitter) - 1;
      const caughtUp = pred.advance(target, this.sampleInput, this.onFreshStep, this.predicting);
      // A reset only helps with a newer authoritative state; otherwise keep catching up 8 steps a frame.
      if (!caughtUp && pred.authTick !== this.lastResetAuthTick) this.resync(now, pred);
      this.sendInputs(pred);
      alpha = clamp(target - pred.predTick, 0, 1);
    }

    this.events.update(renderTick);
    this.pending.prune(renderTick);
    this.buildView(pred, init, renderTick, alpha);
    return v;
  }

  // ---- Messages -------------------------------------------------------------------------------

  private onJoined(m: Extract<ServerMsg, { t: 'joined' }>, at: number): void {
    const prev = this.session;
    if (prev?.code !== m.code) {
      this.exitRoom();
      this.focus = NO_SLOT;
    }
    this.session = { code: m.code, slot: m.slot, spectator: m.spectator };
    this.events.setLocalSlot(this.localSlot);
    this.clock.startPings(at);
    // Promoted from spectator (or moved) inside the current world: predict the new slot from the
    // authoritative state already received, without waiting for the next round.
    if (prev && prev.code === m.code && this.pred && this.pred.localSlot !== this.localSlot) this.rebindLocal(this.pred);
    this.bus.emit('joined', { code: m.code, slot: m.slot, spectator: m.spectator, isHost: m.isHost });
  }

  /** Rebuilds prediction for a new local slot from the current authoritative world. */
  private rebindLocal(old: Prediction): void {
    const init = this.roundInit;
    if (!init) return;
    const pred = new Prediction(init.cfg, init.roundId, init.startRoomTick, this.localSlot, this.errors);
    pred.onError = this.errorProbe;
    if (old.hasAuth) pred.adoptAuthWorld(old.authWorld, old.authTick);
    pred.lead = old.lead;
    this.pred = pred;
    this.lastSampledTick = this.lastSentTick = Math.max(old.predTick, pred.predTick);
    this.lastSent.tick = -1;
    this.lastResetAuthTick = -1;
    this.hasLocalRender = false;
    this.opening = false;
    if (this.autoplay) this.armAutoplay(pred);
  }

  private onRoom(room: RoomStateDTO): void {
    const prev = this.roomState?.phase;
    this.roomState = room;
    if (room.phase === RoomPhase.LOBBY && prev !== undefined && prev !== RoomPhase.LOBBY) {
      this.replay.clearMatch();
      this.instantSlice = this.potmSlice = null;
    }
    this.bus.emit('room', room);
  }

  private onRound(init: RoundInitDTO): void {
    this.events.startRound();
    this.roundInit = init;
    this.roundToRoom = init.startRoomTick;
    const prevLead = this.pred?.lead ?? Number.NaN;
    const pred = new Prediction(init.cfg, init.roundId, init.startRoomTick, this.localSlot, this.errors);
    pred.onError = this.errorProbe;
    // The link has not changed: keep the converged lead rather than restarting from the formula.
    if (Number.isFinite(prevLead)) pred.lead = prevLead;
    this.pred = pred;
    this.snapshots.clear();
    this.interp.reset();
    this.replay.setRound(init);
    this.pending.clear();
    this.lastSampledTick = this.lastSentTick = pred.predTick;
    this.lastSent.tick = -1;
    this.lateBefore = Number.NEGATIVE_INFINITY;
    this.lastResetAuthTick = -1;
    this.gameOver = false;
    this.opening = true;
    this.hasLocalRender = false;
    if (this.autoplay) this.armAutoplay(pred);
    if (this.ghostSpectate) {
      this.ghostSpectate = false;
      this.focus = NO_SLOT;
    }
    this.bus.emit('round:init', init);
  }

  private onRoundEnd(end: RoundEndDTO): void {
    this.instantSlice = this.replay.captureHighlight(end) ?? this.replay.highlight(end.roundId);
    this.bus.emit('round:end', end);
  }

  private onMatchEnd(end: MatchEndDTO): void {
    this.potmSlice = this.replay.playOfMatch(end.playOfMatch);
    this.bus.emit('match:end', end);
  }

  private onKicked(m: Extract<ServerMsg, { t: 'kicked' }>): void {
    this.exitRoom();
    this.bus.emit('error', { code: 'KICKED', msg: m.reason });
  }

  private onSnapshot(data: Uint8Array, at: number): void {
    const init = this.roundInit;
    const pred = this.pred;
    if (!init || !pred) return;
    let header: SnapshotHeader;
    let snap: Snapshot;
    try {
      header = readSnapshotHeader(data);
      if (header.roundId !== (init.roundId & 0xff)) return;
      const latest = this.snapshots.latest;
      if (latest && header.roomTick <= latest.roomTick) return;
      snap = decodeSnapshot(data, init.cfg.mapId);
    } catch {
      this.badSnapshots++;
      return;
    }
    this.clock.addSnapshot(snap.roomTick, at);
    this.snapshots.push(snap);
    this.replay.push(snap, data);
    this.roundToRoom = snap.roomTick - snap.roundTick;
    this.events.onServerEvents(snap.events, this.roundToRoom);
    const local = this.localSlot;
    for (const ev of snap.events) {
      if (local === NO_SLOT) break;
      if (ev.type === EV.KO && ev.a === local) this.replay.captureBonkCam(ev.a, ev.b);
      else if (ev.type === EV.HIT && ev.b === local) this.hitAbsorbUntil = at + PREDICTION.hitAbsorbMs;
    }
    pred.applySnapshot(snap);
    if (Number.isNaN(pred.lead)) pred.initLead(this.clock.rtt, this.clock.jitter);
    else if (local !== NO_SLOT && this.worldSteps()) pred.nudgeLead(snap.inputSlack, this.clock.jitter);
  }

  /** GAME! grade (§12.8): the final KO of the match's last round. */
  /**
   * GAME! (grade 'game') when the match's last round ends: on its final KO (Royale, a Potato
   * blast), and on its ROUND_OVER however it ended (the time cap's DRAW, a Crown Grab goal).
   */
  private watchEvent(ev: SimEvent): void {
    const final = ev.type === EV.KO ? (ev.d & KoFlag.FINAL) !== 0 : ev.type === EV.ROUND_OVER;
    if (!final) return;
    const room = this.roomState;
    const cfg = this.roundInit?.cfg;
    if (room && cfg && cfg.modeId !== ModeId.LOBBY && cfg.modeId !== ModeId.PODIUM && room.roundIndex === room.totalRounds - 1) this.gameOver = true;
  }

  private exitRoom(): void {
    this.session = null;
    this.roomState = null;
    this.roundInit = null;
    this.pred = null;
    this.snapshots.clear();
    this.interp.reset();
    this.events.reset();
    this.events.setLocalSlot(NO_SLOT);
    this.replay.reset();
    this.instantSlice = this.potmSlice = null;
    this.pending.clear();
    this.clock.reset();
  }

  // ---- Prediction plumbing --------------------------------------------------------------------

  private phase(): RoomPhase {
    return this.roomState?.phase ?? RoomPhase.LOBBY;
  }

  private worldSteps(): boolean {
    return STEPPING_PHASES.includes(this.phase());
  }

  private shouldPredict(pred: Prediction): boolean {
    const slot = this.localSlot;
    if (slot === NO_SLOT || !PREDICTED_PHASES.includes(this.phase())) return false;
    const auth = pred.authWorld.beans[slot];
    if (!auth.active || UNPREDICTED_STATES.includes(auth.state)) return false;
    const p = this.roomState?.players[slot];
    // AFK and reserved-slot takeovers: a bot drives the bean, except a reserving human's ghost.
    return !(p && (p.afk || (p.isBot && !auth.out)));
  }

  /**
   * A round that has just started is fully determined by its config, so prediction steps it fresh
   * from `createWorld` before the first snapshot is reconciled, however early that snapshot came.
   * This is what produces the opening COUNTDOWN, which is time-derived and never sent. A round
   * joined midway skips this and starts from its first snapshot.
   */
  private openRound(now: number, pred: Prediction): void {
    if (!this.worldSteps() || !this.clock.synced) return;
    this.opening = false;
    if (Number.isNaN(pred.lead)) pred.initLead(this.clock.rtt, this.clock.jitter);
    const target = this.clock.estServerTick(now) + pred.lead;
    if (target - pred.predTick > CLIENT_GAME.maxOpeningTicks) return;
    this.predicting = this.shouldPredict(pred);
    let caughtUp = false;
    while (!caughtUp) caughtUp = pred.advance(target, this.sampleInput, this.onFreshStep, this.predicting);
    this.sendInputs(pred);
  }

  private resync(now: number, pred: Prediction): void {
    this.resyncRequested = false;
    this.clock.hardResync();
    const latest = this.snapshots.latest;
    if (latest) this.snapshots.trimBefore(latest.roomTick);
    this.interp.reset();
    this.lastResetAuthTick = pred.authTick;
    pred.hardReset(this.clock.estServerTick(now) + pred.lead, this.predicting);
  }

  /** Local frame for tick `t`; ticks already sampled reuse their stored frame. */
  private readonly sampleInput = (t: number): InputFrame | null => {
    const pred = this.pred;
    const slot = this.localSlot;
    if (!pred || slot === NO_SLOT || this.ghostSpectate) return null;
    if (t <= this.lastSampledTick) return pred.inputs.get(t);
    this.lastSampledTick = t;
    const frame = this.autoplay ? this.botFrame(pred, slot) : this.input?.sample(t, this.aimContext(slot)) ?? null;
    if (t <= this.lateBefore) {
      // Catching up after a stall: the server has already applied this tick with §20.6's fallback
      // (the last frame it got, repeated for up to 6 ticks, then neutral with the same counters), so
      // predict exactly that. Presses sampled meanwhile stay in the counters and land on the next
      // frame that arrives in time, as they do on the server.
      const last = this.lastSent;
      if (last.tick < 0 || t - last.tick > CLIENT_GAME.serverRepeatTicks) return null;
      Object.assign(this.sampled, last);
      this.sampled.tick = t;
      return this.sampled;
    }
    if (!frame) return null;
    Object.assign(this.sampled, frame);
    this.sampled.tick = t;
    return this.sampled;
  };

  private readonly onFreshStep = (events: World['events'], t: number): void => {
    const pred = this.pred;
    if (!pred) return;
    const fresh = this.events.onPredictedEvents(events, t, this.predicting);
    const slot = this.localSlot;
    if (fresh && this.predicting) {
      for (const ev of events) if (ev.type === EV.ITEM_USE && ev.a === slot) this.pending.onItemUse(ev.b, pred.predWorld.beans[slot], pred.inputs.get(t), t);
    }
    this.autoplay?.observe(pred.predWorld);
  };

  private armAutoplay(pred: Prediction): void {
    const bots = this.autoplay;
    const slot = this.localSlot;
    if (!bots) return;
    bots.reset(pred.predWorld);
    if (slot === NO_SLOT) return;
    const cfg = pred.authWorld.cfg;
    const personality = PERSONALITIES[slot % PERSONALITIES.length];
    bots.setBot(slot, { slot, personality, difficulty: Difficulty.NORMAL, seed: (cfg.seed ^ Math.imul(slot + 1, 0x9e3779b1)) >>> 0, name: this.roomState?.players[slot]?.name ?? '' });
  }

  private botFrame(pred: Prediction, slot: number): InputFrame | null {
    const ctx = this.botCtx;
    const players = this.roomState?.players;
    ctx.humanSlots.length = 0;
    for (let i = 0; i < MAX_BEANS; i++) {
      const p = players?.[i];
      ctx.matchScores[i] = p ? p.score : 0;
      if (p && !p.isBot) ctx.humanSlots.push(i);
    }
    ctx.crowned = pred.authWorld.cfg.crownedSlot;
    ctx.teamsOn = pred.authWorld.cfg.teamsOn;
    this.autoplay?.inputsFor(pred.predWorld, ctx, this.botFrames);
    return this.botFrames[slot];
  }

  /** Aim context from the last rendered view (§26). */
  private aimContext(slot: number): AimContext {
    const v = this.view;
    const me = v.beans[slot];
    const a = this.aim;
    a.selfX = me.x;
    a.selfY = me.y;
    a.facing = me.facing;
    const targets = this.aimTargets;
    let n = 0;
    for (const b of v.beans) {
      if (b.slot === slot || !this.inPlay(b) || b.ghost) continue;
      if (n === targets.length) targets.push({ slot: 0, x: 0, y: 0, isTeammate: false });
      const t = targets[n++];
      t.slot = b.slot;
      t.x = b.x;
      t.y = b.y;
      t.isTeammate = me.team !== 0 && b.team === me.team;
    }
    targets.length = n;
    a.targets = targets;
    return a;
  }

  /** Every 2 ticks: the newest frames plus 2 redundant ones, up to 8, consecutive ticks only. */
  private sendInputs(pred: Prediction): void {
    const newest = this.lastSampledTick;
    if (this.localSlot === NO_SLOT || newest - this.lastSentTick < CLIENT_GAME.sendEveryTicks || !pred.inputs.get(newest)) return;
    const want = Math.min(CLIENT_GAME.maxFramesPerBatch, newest - this.lastSentTick + CLIENT_GAME.redundantFrames);
    let first = newest;
    while (newest - first + 1 < want && pred.inputs.get(first - 1)) first--;
    const buf = this.sendBuf;
    buf.length = 0;
    for (let t = first; t <= newest; t++) {
      const f = pred.inputs.get(t);
      if (f) buf.push(f);
    }
    this.net.sendInputs(buf, this.viewTick);
    this.lastSentTick = newest;
    // The server's fallback repeats the newest frame it actually used, so stand-ins for late ticks
    // (which it discards) never become `lastSent`.
    for (let i = buf.length - 1; i >= 0; i--) {
      if (buf[i].tick <= this.lateBefore) break;
      if (buf[i].tick > this.lastSent.tick) {
        Object.assign(this.lastSent, buf[i]);
        break;
      }
    }
  }

  // ---- View assembly --------------------------------------------------------------------------

  private inPlay(b: BeanView): boolean {
    return b.active && !b.out && b.state !== BS.FALLING;
  }

  private buildView(pred: Prediction, init: RoundInitDTO, renderTick: number, alpha: number): void {
    const v = this.view;
    const cfg = init.cfg;
    const room = this.roomState;
    const local = this.localSlot;
    v.source = 'online';
    v.roomPhase = this.phase();
    v.roundIndex = room?.roundIndex ?? init.roundIndex;
    v.totalRounds = room?.totalRounds ?? 0;
    setRoundInfo(v, cfg, init.roundId);
    v.renderTick = renderTick;
    v.localTick = pred.predTick - 1 + alpha;
    v.roundTick = v.localTick - this.roundToRoom;
    roundClock(cfg.mapId, cfg.modeId, v.roundTick, cfg.capTickOverride, v.clock);
    v.localSlot = local;

    const bracket = this.interp.prepare(this.snapshots);
    for (let slot = 0; slot < MAX_BEANS; slot++) {
      const bv = v.beans[slot];
      if (slot === local && this.predicting) {
        beanViewFromBean(bv, pred.predWorld.beans[slot]);
        pred.localPos(alpha, this.localRaw);
        this.placeLocal(bv, pred, true);
      } else if (this.interp.sampleBean(slot, bv)) {
        if (slot === local) {
          this.localRaw.x = bv.x;
          this.localRaw.y = bv.y;
          this.localRaw.z = bv.z;
          this.placeLocal(bv, pred, false);
        }
      } else {
        bv.active = false;
      }
      const p = room?.players[slot] ?? null;
      setBeanIdentity(bv, p);
      if (!p) bv.team = cfg.teams[slot] ?? 0;
      bv.isLocal = slot === local;
      bv.crowned = slot === cfg.crownedSlot;
    }

    const n = this.interp.sampleEntities(v.ents);
    resizeEntityViews(v.ents, n + this.pending.size);
    this.pending.write(v.ents, n, v.localTick);

    copyMapState(v.map, pred.predWorld.map);
    MAPS[cfg.mapId].hazardPose(pred.predWorld, v.roundTick, v.hazard);
    fillModeView(v, bracket.a ? bracket.a.mode : pred.authWorld.mode);
    v.focusSlot = this.focusSlot();
    v.replay = null;
    v.grade = frameGrade(v, this.gameOver);
    this.fillNetView();
  }

  /**
   * Final local position: the source position (prediction or interpolation) plus the visual
   * offset. A switch between the two sources is folded into the offset so the bean never pops.
   */
  private placeLocal(bv: BeanView, pred: Prediction, predicted: boolean): void {
    const raw = this.localRaw;
    const o = pred.visualOffset;
    if (this.hasLocalRender && predicted !== this.renderedPredicted) {
      const last = this.lastLocalRender;
      pred.addVisualOffset(last.x - raw.x - o.x, last.y - raw.y - o.y, last.z - raw.z - o.z);
    }
    bv.x = raw.x + o.x;
    bv.y = raw.y + o.y;
    bv.z = raw.z + o.z;
    this.lastLocalRender.x = bv.x;
    this.lastLocalRender.y = bv.y;
    this.lastLocalRender.z = bv.z;
    this.hasLocalRender = true;
    this.renderedPredicted = predicted;
  }

  private focusSlot(): number {
    const beans = this.view.beans;
    if (this.focus !== NO_SLOT && this.inPlay(beans[this.focus])) return this.focus;
    const local = this.localSlot;
    if (local !== NO_SLOT && beans[local].active && !this.ghostSpectate) return local;
    for (const b of beans) if (this.inPlay(b)) return b.slot;
    return NO_SLOT;
  }

  private fillNetView(): void {
    const n = this.view.net;
    n.online = this.net.status === 'online';
    n.rtt = this.clock.rtt;
    n.jitter = this.clock.jitter;
    if (this.errors.count !== this.predErrCount) {
      this.predErrCount = this.errors.count;
      this.predErrP95 = this.errors.percentile(0.95);
    }
    n.predErrP95 = this.predErrP95;
  }
}
